import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemText
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

interface AttachmentItem {
  id: number;
  file: string;
  uploaded_at: string;
}

interface MultiFileUploaderProps {
  service: any;
  activityId: number;
  attachments: AttachmentItem[];
  onRefresh?: () => void;
  disableEdit?: boolean;
}

export const extractFilename = (path: string | null): string => {
  if (!path) return '';
  try {
    const url = new URL(path);
    path = url.pathname;
  } catch {
    // Fallback: URL parsing failed, use the raw string path
  }
  return path.split('/').pop() || path;
};

const MultiFileUploader: React.FC<MultiFileUploaderProps> = ({
  service,
  activityId,
  attachments = [],
  onRefresh,
  disableEdit = false
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  /* ------------------ State ------------------ */
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isMenuOpen = Boolean(anchorEl);
  const totalAttachments = attachments.length;

  /* ------------------ Menu Handlers ------------------ */
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /* ------------------ Upload Handlers ------------------ */
  const handleOpenUploadDialog = () => {
    setIsUploadDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseUploadDialog = () => {
    setSelectedFile(null);
    setIsUploadDialogOpen(false);
  };

  const handleFileDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  };

  const handleConfirmUpload = () => {
    if (selectedFile && activityId) {
      service.attachFiles(selectedFile, 'file', activityId).then(() => {
        enqueueSnackbar(t('attachment-modal.upload-success', 'Attachment uploaded.'), {
          variant: 'success'
        });
        setIsUploadDialogOpen(false);
        setSelectedFile(null);
        if (onRefresh) onRefresh();
      });
    }
  };

  /* ------------------ Action Handlers ------------------ */
  const handleDownload = (attachmentId: number | undefined | null) => {
    if (!attachmentId) {
      enqueueSnackbar('Impossibile scaricare: ID allegato non valido.', { variant: 'error' });
      return;
    }
    if (activityId) {
      service.downloadAttachment(activityId, String(attachmentId));
    }
    handleMenuClose();
  };

  const handleDelete = (attachmentId: number | undefined | null) => {
    if (!attachmentId) {
      enqueueSnackbar('Impossibile eliminare: ID allegato non valido.', { variant: 'error' });
      return;
    }
    if (activityId) {
      service
        .deleteAttachment(activityId, String(attachmentId))
        .then(() => {
          enqueueSnackbar(t('attachment-modal.delete-success', 'Attachment removed.'), {
            variant: 'success'
          });
          handleMenuClose();
          if (onRefresh) onRefresh();
        })
        .catch(() => {
          enqueueSnackbar('Deletion failed.', { variant: 'error' });
        });
    }
  };

  return (
    <Box id="multi-file-uploader-box" sx={{ display: 'inline-block' }}>
      <Tooltip title={t('actions.manage-attachments', 'Gestisci allegati')} enterDelay={200}>
        <Button
          variant="text"
          size="small"
          startIcon={<AttachFileIcon fontSize="small" />}
          onClick={handleMenuOpen}
          sx={{ minWidth: 'auto', fontWeight: 600 }}>
          {totalAttachments > 0 ? totalAttachments : '+'}
        </Button>
      </Tooltip>

      {/* ===== MENU A TENDINA (DROPDOWN) ===== */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { minWidth: 240, maxWidth: 320, mt: 0.5, boxShadow: 3 }
        }}>
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
          {t('global.attachments', 'Allegati')} ({totalAttachments})
        </Typography>

        {/* Lista dei file correnti */}
        {attachments.map((att) => {
          const filename = extractFilename(att.file);
          return (
            <MenuItem
              key={att.id}
              sx={{ py: 1, gap: 1, justifyContent: 'space-between' }}
              onClick={() => handleDownload(att.id)}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, gap: 1 }}>
                <InsertDriveFileIcon fontSize="small" color="action" />
                <ListItemText
                  primary={filename}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                    title: filename
                  }}
                />
              </Box>

              {/* Azione di cancellazione rapida dentro il menu */}
              {!disableEdit && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents the download handler from firing
                    handleDelete(att.id); // Fires deletion using the unique ID
                  }}
                  sx={{ ml: 1 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </MenuItem>
          );
        })}

        {totalAttachments === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 1.5, fontStyle: 'italic' }}>
            Nessun file presente
          </Typography>
        )}

        {/* Pulsante in fondo al menu per caricare un NUOVO file */}
        {!disableEdit && (
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', mt: 0.5 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handleOpenUploadDialog}>
              {t('actions.upload-attachment', 'Aggiungi file')}
            </Button>
          </Box>
        )}
      </Menu>

      {/* ===== DIALOG DI CARICAMENTO (DROP-ZONE) ===== */}
      <Dialog open={isUploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Carica File
          </Typography>
          <IconButton onClick={handleCloseUploadDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          <Box
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.onchange = (e) => {
                const files = (e.target as HTMLInputElement).files;
                if (files) handleFileDrop(Array.from(files));
              };
              input.click();
            }}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: selectedFile ? 'success.lighter' : 'transparent',
              '&:hover': { bgcolor: 'grey.50' }
            }}>
            <Typography variant="body2">
              {selectedFile
                ? `File: ${selectedFile.name}`
                : 'Clicca qui per selezionare un file da caricare'}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={handleCloseUploadDialog}>
            {t('actions.cancel', 'Annulla')}
          </Button>
          <Button variant="contained" onClick={handleConfirmUpload} disabled={!selectedFile}>
            {t('actions.upload-attachment', 'Invia')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultiFileUploader;
